import { inject } from '@angular/core';
import type { ResolveFn, ActivatedRouteSnapshot } from '@angular/router';
import { Router } from '@angular/router';
import { catchError, of, map } from 'rxjs';
import { UserManagementService } from '../../../core/services/user-management.service';
import { AuthService } from '../../../core/services/auth.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import type { IUser } from '../../../shared/models/user.model';
import {
  validateAuthentication,
  getMarketIdFromContext,
  handleResolverError,
  validateManagerData,
  validateQueryParam,
  RESOLVER_ERROR_MESSAGES
} from './resolver-helpers';

export interface SubManagerCyclesResolverData {
  manager: IUser;
  currentUser: IUser;
  marketId: number;
}

/**
 * Resolver for sub-manager-cycles route
 *
 * Pre-loads manager details and validates access before activating the route.
 * This resolver ensures that:
 * - User is authenticated
 * - Market context is available
 * - Manager PRID is valid
 * - Manager data can be loaded from the API
 *
 * @param route - Activated route snapshot containing query parameters
 * @returns Observable of resolver data or null if validation fails
 */
export const subManagerCyclesResolver: ResolveFn<SubManagerCyclesResolverData | null> = (
  route: ActivatedRouteSnapshot
) => {
  const userManagementService = inject(UserManagementService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const snackbarService = inject(SnackbarService);

  // Validate manager PRID parameter
  const managerPrid = route.queryParamMap.get('prid');
  if (!validateQueryParam(managerPrid, RESOLVER_ERROR_MESSAGES.INVALID_MANAGER_DETAILS, snackbarService, router)) {
    return of(null);
  }

  // Validate user authentication
  const currentUser = validateAuthentication(authService, snackbarService, router);
  if (!currentUser) {
    return of(null);
  }

  // Get market ID from context
  const marketId = getMarketIdFromContext(authService, currentUser, snackbarService, router);
  if (!marketId) {
    return of(null);
  }

  // Resolve manager details by PRID
  return userManagementService.getUserByPRID(managerPrid).pipe(
    map(manager => {
      if (!validateManagerData(manager, snackbarService, router)) {
        return null;
      }

      return {
        manager: manager!,
        currentUser,
        marketId
      };
    }),
    catchError(error =>
      handleResolverError(error, 'Error resolving sub-manager cycles data:', snackbarService, router)
    )
  );
};
